/** Canonical Lazada channel subtype / platform code in OMS. */
export const LAZADA_CHANNEL_CODE = 'LAZADA';

/** O2O business type for marketplace orders (Lazada). */
export const O2O_BUSINESS_TYPE_ECOMMERCE = 'E-COMMERCE';

export const LAZADA_MERGE_AWB_PATH = '/v1/channel-integration/lazada/merge-awb';

export type LazadaConnectionStatus =
  | 'CONNECTED'
  | 'NOT_CONNECTED'
  | 'TEMP_ERROR';

export const LAZADA_TAG_ACTIVE_STYLE: Record<string, string> = {
  backgroundColor: '#ecfdf5',
  color: '#047857',
};

export const LAZADA_TAG_INACTIVE_STYLE: Record<string, string> = {
  backgroundColor: '#fef2f2',
  color: '#dc2626',
};

export const LAZADA_TAG_NOT_CONNECTED_STYLE: Record<string, string> = {
  backgroundColor: '#FEF3C7',
  color: '#a16207',
};

/** Normalize channel / platform string for comparison. */
export function normalizeChannelPlatformCode(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return '';
  }
  return String(value).trim().toUpperCase();
}

export function isLazadaChannelCode(code: string | undefined | null): boolean {
  return normalizeChannelPlatformCode(code) === LAZADA_CHANNEL_CODE;
}

/** Row shape from online store list / API (multiple field names). */
export interface OnlineStoreChannelLike {
  channelSubTypeCode?: string;
  channelType?: string;
  channelPlatform?: string;
}

export function resolveOnlineStoreChannelCode(
  row: OnlineStoreChannelLike | undefined | null,
): string {
  if (!row) return '';
  return (
    row.channelSubTypeCode ?? row.channelType ?? row.channelPlatform ?? ''
  );
}

export function isLazadaOnlineStore(
  row: OnlineStoreChannelLike | undefined | null,
): boolean {
  return isLazadaChannelCode(resolveOnlineStoreChannelCode(row));
}

export function isLazadaO2oOrder(
  businessType: string | undefined | null,
  channel: string | undefined | null,
): boolean {
  return (
    normalizeChannelPlatformCode(businessType) ===
      O2O_BUSINESS_TYPE_ECOMMERCE &&
    isLazadaChannelCode(channel)
  );
}

export function buildLazadaMergeAwbUrl(basePath: string): string {
  const trimmed = (basePath || '').replace(/\/+$/, '');
  return `${trimmed}${LAZADA_MERGE_AWB_PATH}`;
}

export function parseOuterOrderNosToNumericIds(
  orders: ReadonlyArray<{
    outerOrderNo?: unknown;
    outer_order_no?: unknown;
  }>,
): number[] {
  return orders
    .map((o) => {
      const no = o.outerOrderNo ?? o.outer_order_no ?? '';
      return no != null && no !== '' ? Number(no) : Number.NaN;
    })
    .filter((n) => !Number.isNaN(n));
}

export function defaultOnlineStoreStatusSeverity(
  active: boolean,
): 'success' | 'danger' {
  return active ? 'success' : 'danger';
}

export function lazadaOnlineStoreStatusSeverity(
  active: boolean,
  connectionStatus: LazadaConnectionStatus | undefined,
): 'success' | 'warning' | 'danger' {
  if (active && connectionStatus === 'CONNECTED') {
    return 'success';
  }
  if (
    active &&
    (connectionStatus === 'NOT_CONNECTED' || connectionStatus === undefined)
  ) {
    return 'warning';
  }
  return 'danger';
}

export function defaultOnlineStoreStatusStyle(
  active: boolean,
): Record<string, string> {
  return active
    ? { ...LAZADA_TAG_ACTIVE_STYLE }
    : { ...LAZADA_TAG_INACTIVE_STYLE };
}

export function lazadaOnlineStoreStatusStyle(
  active: boolean,
  connectionStatus: LazadaConnectionStatus | undefined,
): Record<string, string> {
  if (active && connectionStatus === 'CONNECTED') {
    return { ...LAZADA_TAG_ACTIVE_STYLE };
  }
  if (
    active &&
    (connectionStatus === 'NOT_CONNECTED' || connectionStatus === undefined)
  ) {
    return { ...LAZADA_TAG_NOT_CONNECTED_STYLE };
  }
  return { ...LAZADA_TAG_INACTIVE_STYLE };
}

/** Form + API snapshot when deciding Lazada-only UI (product list, credentials). */
export interface OnlineStoreFormLazadaContextLike {
  formData?: { channelSubTypeCode?: string } | null;
  originalApiData?: {
    channelSubTypeCode?: string;
    channelType?: string;
  } | null;
}

export function isLazadaOnlineStoreFormContext(
  ctx: OnlineStoreFormLazadaContextLike,
): boolean {
  const fd = ctx.formData;
  const orig = ctx.originalApiData;
  return (
    isLazadaChannelCode(fd?.channelSubTypeCode) ||
    isLazadaChannelCode(orig?.channelSubTypeCode) ||
    isLazadaChannelCode(orig?.channelType)
  );
}

export function parseLazadaQuantityFromProductRecord(
  record:
    | { lazadaQuantity?: unknown; lazada_quantity?: unknown }
    | null
    | undefined,
): number {
  if (record == null) {
    return 0;
  }
  const raw = record.lazadaQuantity ?? record.lazada_quantity ?? 0;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

/** Lazada product listing navigation (requires shop + Lazada channel). */
export function canOpenLazadaProductListing(
  shopId: number | null | undefined,
  channelType: string | null | undefined,
): boolean {
  return (
    shopId != null &&
    !Number.isNaN(Number(shopId)) &&
    isLazadaChannelCode(channelType)
  );
}
